import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Row, Col, Panel, Button, Label, Tabs, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';

const STATUS_LABELS = {
    active: { text: 'Actif', style: 'success' },
    trialing: { text: 'Essai', style: 'info' },
    past_due: { text: 'En retard', style: 'warning' },
    unpaid: { text: 'Impayé', style: 'danger' },
    incomplete: { text: 'Incomplet', style: 'warning' },
    incomplete_expired: { text: 'Expiré', style: 'default' },
    canceled: { text: 'Annulé', style: 'default' }
};

const TABS = [
    { key: 'active', label: 'Actifs', filter: 'active' },
    { key: 'trialing', label: 'En essai', filter: 'trialing' },
    { key: 'unpaid', label: 'Impayés', filter: 'unpaid' },
    { key: 'cancel_at_period_end', label: 'Résiliation prévue', filter: 'cancel_at_period_end' },
    { key: 'canceled', label: 'Annulés', filter: 'canceled' },
    // Checkouts whose first payment never went through (incomplete /
    // incomplete_expired): kept out of "Tous" and "Annulés", never subscribers.
    { key: 'abandoned', label: 'Paiements abandonnés', filter: 'abandoned' },
    { key: 'all', label: 'Tous', filter: null }
];

const renderStatus = (s) => {
    if (!s) return <span className="text-muted">—</span>;
    const m = STATUS_LABELS[s] || { text: s, style: 'default' };
    return <Label bsStyle={m.style}>{m.text}</Label>;
};

const Subscriptions = () => {
    const [activeTab, setActiveTab] = useState('active');
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({
        total: 0, active: 0, trialing: 0, unpaid: 0, canceled: 0, other: 0, abandoned: 0,
        cancelAtPeriodEnd: 0, mrrEur: 0, churnThisMonth: 0
    });
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchList = useCallback(async (tabKey) => {
        const tab = TABS.find(t => t.key === tabKey) || TABS[0];
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ status: tab.filter || 'all', limit: '500' });
            const res = await fetchApi(`/subscriptions/list?${params.toString()}`, 'GET');
            if (res && res.success) {
                setRows(res.data.rows || []);
                setSummary(res.data.summary || {});
                setTotal(res.data.total || 0);
            } else {
                setError((res && res.message) || 'Erreur de chargement');
            }
        } catch (err) {
            setError(`Erreur: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(activeTab); }, [activeTab, fetchList]);

    const truncatedNotice = useMemo(() => total > rows.length && (
        <p className="text-muted">
            <small>Affichage des {rows.length} plus récents sur {total}. Filtrer par statut pour réduire.</small>
        </p>
    ), [total, rows.length]);

    const renderTable = () => {
        if (loading) return <p className="text-muted">Chargement...</p>;
        if (rows.length === 0) return <p className="text-muted">Aucun abonnement dans cette catégorie.</p>;
        return (
            <table className="table table-bordered table-hover table-condensed">
                <thead>
                    <tr>
                        <th>Patient</th>
                        <th>Contact</th>
                        <th>Statut</th>
                        <th>Offre</th>
                        <th style={{ textAlign: 'right' }}>Montant</th>
                        <th>Période en cours</th>
                        <th>Résiliation prévue</th>
                        <th>Annulé le</th>
                        <th>Stripe sub ID</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => {
                        const s = r.subscription || {};
                        return (
                            <tr key={String(r._id)}>
                                <td>
                                    <Link to={`/users/detail/${r._id}`}>
                                        {r.name || r.email || 'Patient'}
                                    </Link>
                                </td>
                                <td>
                                    <small>
                                        {r.email && <div>{r.email}</div>}
                                        {r.phone && <div>{r.phone}</div>}
                                    </small>
                                </td>
                                <td>{renderStatus(s.status)}</td>
                                <td>
                                    <small>{s.priceLabel || <span className="text-muted">—</span>}</small>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {s.monthlyAmountEur ? `${s.monthlyAmountEur.toFixed(2)} €/mois` : '—'}
                                </td>
                                <td>{s.currentPeriodEnd ? dateFormat(s.currentPeriodEnd, 'dd/mm/yyyy') : '—'}</td>
                                <td style={{ textAlign: 'center' }}>
                                    {s.cancelAtPeriodEnd
                                        ? <Label bsStyle="warning">Oui</Label>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td>{s.canceledAt ? dateFormat(s.canceledAt, 'dd/mm/yyyy') : '—'}</td>
                                <td>
                                    <small style={{ fontFamily: 'monospace' }}>
                                        {s.stripeSubscriptionId
                                            ? s.stripeSubscriptionId.slice(0, 14) + '…'
                                            : '—'}
                                    </small>
                                </td>
                                <td>
                                    <Link to={`/users/detail/${r._id}`} className="btn btn-info btn-xs">Voir</Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>
                        Abonnements{' '}
                        <Link to="/subscriptions/attribution" className="btn btn-default btn-sm" style={{ verticalAlign: 'middle' }}>
                            Tracking (sources & médecins)
                        </Link>
                    </h2>
                    <p className="text-muted">
                        Vue lecture-seule des abonnements Feeli. Synchronisé via webhooks Stripe
                        sur <code>user.subscription</code>. KPIs calculés sur l'ensemble des abonnés,
                        indépendamment du filtre actif.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger">{error}</div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Abonnés actifs</h5>
                        <h2 style={{ margin: 0, color: '#5cb85c' }}>{summary.active + summary.trialing}</h2>
                        <small className="text-muted">{summary.active} actif · {summary.trialing} essai</small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>MRR estimé</h5>
                        <h2 style={{ margin: 0 }}>{(summary.mrrEur || 0).toFixed(2)} €</h2>
                        <small className="text-muted">Active + essai × prix mensuel</small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Résiliation prévue</h5>
                        <h2 style={{ margin: 0, color: '#f0ad4e' }}>{summary.cancelAtPeriodEnd}</h2>
                        <small className="text-muted">Actif/essai avec annulation programmée</small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Churn ce mois</h5>
                        <h2 style={{ margin: 0, color: '#d9534f' }}>{summary.churnThisMonth}</h2>
                        <small className="text-muted">
                            Annulés depuis le 1er · {summary.abandoned} paiement(s) abandonné(s) au total
                        </small>
                    </Panel.Body></Panel>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Body style={{ overflowX: 'auto' }}>
                            <Tabs id="subscriptions-tabs" activeKey={activeTab} onSelect={setActiveTab}>
                                {TABS.map(t => (
                                    <Tab key={t.key} eventKey={t.key} title={t.label}>
                                        <div style={{ paddingTop: 15 }}>
                                            {truncatedNotice}
                                            {renderTable()}
                                        </div>
                                    </Tab>
                                ))}
                            </Tabs>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default Subscriptions;
